import * as React from "react";
import { Loader } from "@/components/ui/loader";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { useGetFileDetailsQuery } from "@/api/files";
import { ChatInterface } from "./ChatInterface";
import { useParams } from "react-router";
import ChatLayout from "./ChatLayout";

const ChatWrapper = () => {
  const { systemId = "", conversationId = "" } = useParams<{ systemId: string; conversationId: string }>();

  const { selectedFiles, setCurrentPdfId } = useCurrentPdf();
  const { data: chatContext, isLoading, error } = useGetFileDetailsQuery({ fse_id: systemId, depth: 200 });

  const allFileIds = React.useMemo(() => {
    if (!chatContext) return [];
    const ids: string[] = [];
    const collect = (item: typeof chatContext) => {
      if (item.type === "file") {
        ids.push(item.id);
      } else if (item.type === "folder") {
        Object.values(item.items).forEach(collect);
      }
    };
    collect(chatContext);
    return ids;
  }, [chatContext]);

  const excludeFileIds = React.useMemo(() => {
    if (selectedFiles.length === 0) return [];
    const selectedSet = new Set(selectedFiles);
    return allFileIds.filter(id => !selectedSet.has(id));
  }, [allFileIds, selectedFiles]);

  const hasInitializedPdf = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitializedPdf.current && selectedFiles.length > 0) {
      setCurrentPdfId(selectedFiles[0]);
      hasInitializedPdf.current = true;
    }
  }, [selectedFiles, setCurrentPdfId]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-red-500">An error occurred while loading the chat context.</p>
      </div>
    );
  }

  if (!chatContext) {
    return null;
  }

  return (
    <div className="flex h-full w-full">
      <ChatInterface
        fsEntry={chatContext}
        systemId={systemId}
        key={conversationId}
        conversationId={conversationId}
        excludeFileIds={excludeFileIds}
      />
    </div>
  );
};

export const ChatView = () => (
  <ChatLayout>
    <ChatWrapper />
  </ChatLayout>
);
